import * as Sentry from '@sentry/react'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { QueryCache, QueryClient } from '@tanstack/react-query'
import axios, { type AxiosError, type AxiosRequestConfig } from 'axios'
import { getAuth } from 'firebase/auth'
import { Logger } from '@/components/Logger'
import { publicConfig } from '@/config/publicConfig'
import { LocalStorageKeys } from '@/constants/localstorage'
import { client as kubbClient } from '@/hooks/v1/.kubb/client'
import { getApiKey } from '@/utils/apiKeyAuth'
import { getApiErrorMessage } from '@/utils/apiErrorUtils'
import { isExpectedNotFound } from '@/utils/sentryErrorFilters'

const apiBaseUrl = publicConfig.api.baseUrl || 'https://dev-dora.co'

export const apiAxiosTransport = axios.create({
  baseURL: apiBaseUrl,
  maxRedirects: 5, // Default is 1, set to 0 to disable redirects
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams()

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Serialize arrays as repeated parameters: param=value1&param=value2
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              searchParams.append(key, String(item))
            }
          })
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })

      return searchParams.toString()
    },
  },
})

const apiClient = apiAxiosTransport

// Generated hooks share this transport so authentication and payload normalization stay consistent.
kubbClient.setConfig({
  baseURL: apiBaseUrl,
  transport: apiAxiosTransport,
})

const normalizeHostname = (value: string): string => {
  const trimmedValue = value.trim()
  if (!trimmedValue) return ''

  try {
    const urlValue = trimmedValue.includes('://') ? trimmedValue : `https://${trimmedValue}`
    return new URL(urlValue).hostname.toLowerCase()
  } catch {
    return trimmedValue.split('/')[0].split(':')[0].toLowerCase()
  }
}

const apiDomainHostname = normalizeHostname(publicConfig.api.domain)

const isApiDomainRequest = (request: AxiosRequestConfig): boolean => {
  if (!apiDomainHostname) return false

  try {
    const resolvedUrl = new URL(request.url ?? '', request.baseURL ?? apiBaseUrl)
    return resolvedUrl.hostname.toLowerCase() === apiDomainHostname
  } catch {
    return false
  }
}

const isCanceledRequestError = (error: unknown): boolean => {
  if (axios.isCancel(error)) return true
  if (!error || typeof error !== 'object') return false
  const maybeError = error as { code?: string; name?: string }
  return maybeError.code === 'ERR_CANCELED' || maybeError.name === 'CanceledError'
}

// JSON cannot encode bigint. Reject unsafe conversions instead of silently changing quantities;
// recurse only into plain objects so special payloads such as FormData remain intact.
const normalizeBigIntPayload = (value: unknown): unknown => {
  if (typeof value === 'bigint') {
    const asNumber = Number(value)
    if (!Number.isSafeInteger(asNumber)) {
      throw new TypeError(`BigInt value ${value.toString()} exceeds Number.MAX_SAFE_INTEGER`)
    }
    return asNumber
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeBigIntPayload(item))
  }

  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nestedValue]) => [
        key,
        normalizeBigIntPayload(nestedValue),
      ]),
    )
  }

  return value
}

apiClient.interceptors.response.use(
  async (response) => {
    const requestId = Math.random().toString(36).substr(2, 9)

    // Don't throw on missing data, just log and return response
    if (!response?.data) {
      Logger.warn(
        `[${requestId}] DEBUG INTERCEPTOR: No response data received for ${response.config.url}`,
        response,
      )
    }

    return response
  },
  (error) => {
    const response = error.response
    const data = response?.data
    const requestId = Math.random().toString(36).substr(2, 9)

    if (isCanceledRequestError(error)) {
      Logger.log(
        `[${requestId}] DEBUG INTERCEPTOR: Request canceled for ${error.config?.url ?? 'unknown URL'}`,
      )
      return Promise.reject(error)
    }

    // Handle network errors (no response received)
    if (!response) {
      Logger.error(
        `[${requestId}] DEBUG INTERCEPTOR: Network error for ${error.config?.url}:`,
        error.message,
      )
      Sentry.captureMessage(`Network error: ${error.message}`, {
        extra: {
          error: error,
          errorMessage: error.message,
          code: error.code,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
          },
        },
      })
      // Don't throw - let the client function handle error normalization
      Logger.warn(
        `[${requestId}] DEBUG INTERCEPTOR: Network error occurred for ${error.config?.url}, will be handled by client function`,
      )
      return Promise.reject(error)
    }

    // Log API errors before preserving axios's rejected-response contract. Kubb's generated
    // clients rely on the rejection to turn non-2xx responses into ResponseError instances.
    if (data?.message || data?.error || data?.error_msg || data?.detail) {
      const message = getApiErrorMessage(error, `HTTP ${response.status}`)
      if (!isExpectedNotFound(error.config?.url, response.status)) {
        Sentry.captureMessage(`API error: ${message}`, {
          extra: {
            error: message,
            status: response.status,
            method: error.config?.method,
            // Query strings can contain credentials or personal information.
            url: error.config?.url?.split(/[?#]/)[0],
          },
        })
      }
      Logger.warn(
        `[${requestId}] DEBUG INTERCEPTOR: API returned error response for ${error.config?.url}:`,
        data?.error || data?.message,
      )
    }

    return Promise.reject(error)
  },
)

apiClient.interceptors.request.use(
  async (request) => {
    const auth = getAuth()
    auth.tenantId = publicConfig.firebase.tenantId

    const requestHeaders = request.headers ?? {}
    const headersAsRecord = requestHeaders as Record<string, string>
    const setRequestHeader = (key: string, value: string) => {
      if (
        typeof (requestHeaders as { set?: (key: string, value: string) => void }).set === 'function'
      ) {
        ;(requestHeaders as { set: (key: string, value: string) => void }).set(key, value)
      } else {
        requestHeaders[key] = value
      }
    }
    const existingAuthHeader =
      typeof (requestHeaders as { get?: (key: string) => string | null }).get === 'function'
        ? (requestHeaders as { get: (key: string) => string | null }).get('Authorization')
        : headersAsRecord.Authorization || headersAsRecord.authorization

    if (isApiDomainRequest(request)) {
      setRequestHeader('tenant-id', publicConfig.api.tenantId)
    }

    // Preserve caller-supplied credentials; otherwise API-key mode takes precedence over Firebase.
    if (!existingAuthHeader) {
      const apiKey = getApiKey()
      if (apiKey) {
        setRequestHeader('Authorization', `ApiKey ${apiKey}`)
        Logger.log('DEBUG API: using API key auth', { url: request.url })
      } else {
        const token =
          (await auth.currentUser?.getIdToken()) ||
          sessionStorage.getItem(LocalStorageKeys.FIREBASE_TOKEN)
        if (token) {
          setRequestHeader('Authorization', `Bearer ${token}`)
          Logger.log('DEBUG API: using Firebase token auth', { url: request.url })
        }
      }
    }

    if (request.data !== undefined && request.data !== null) {
      request.data = normalizeBigIntPayload(request.data)
    }

    request.headers = requestHeaders
    return request
  },
  (error) => {
    Promise.reject(error).then((r) => {
      Logger.log('DEBUG WS API: Error:', r)
      Sentry.captureMessage(`Axios apiClient interceptors request: ${error.message}`, {
        extra: {
          error: error,
          errorMessage: error.message,
        },
      })
    })
  },
)

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Report failed background refreshes here; initial-load errors are handled by the view.
      if (query.state.data !== undefined) {
        Sentry.captureMessage(`QueryClient: ${error.message}`, {
          extra: {
            error: error,
            errorMessage: error.message,
            query: query,
          },
        })
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 0, //no stale for now
      gcTime: 1000 * 60 * 1, // 1 minutes
    },
  },
})

const persister = createSyncStoragePersister({
  storage: window.sessionStorage,
  key: 'RQ_CACHE_v2',
})

// Export types required by Kubb-generated hooks
export type RequestConfig<TData = any> = AxiosRequestConfig<TData> & {
  skipBusinessErrorCheck?: boolean
}
export type ResponseErrorConfig<T = any> = AxiosError<T>
export type ResponseConfig<T = any> = {
  data: T
  status: number
  statusText: string
  headers: Record<string, string>
}

export type Client = <TData = any, _TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
) => Promise<ResponseConfig<TData>>

export class ApiHttpError<TData = unknown> extends Error {
  status: number
  statusText: string
  data?: TData
  url?: string

  constructor(
    message: string,
    options: {
      status: number
      statusText: string
      data?: TData
      url?: string
    },
  ) {
    super(message)
    this.name = 'ApiHttpError'
    this.status = options.status
    this.statusText = options.statusText
    this.data = options.data
    this.url = options.url
  }
}

// Wrapper function for React Query that ensures consistent error handling
export const client = async <TData = any, _TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
  const requestId = Math.random().toString(36).substr(2, 9)
  const { skipBusinessErrorCheck, ...requestConfig } = config

  try {
    const response = await apiClient.request<TData>(requestConfig)

    // Check for non-2xx status codes
    if (response.status && response.status >= 400) {
      const errorMessage =
        (response.data as any)?.error ||
        (response.data as any)?.error_msg ||
        (response.data as any)?.message ||
        `HTTP ${response.status} error`
      Logger.error(
        `[${requestId}] DEBUG CLIENT: HTTP error ${response.status} for ${config.url}:`,
        errorMessage,
      )
      throw new ApiHttpError(errorMessage, {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        url: config.url,
      })
    }

    // Check for business logic errors in 2xx responses
    if (
      !skipBusinessErrorCheck &&
      response.data &&
      typeof response.data === 'object' &&
      'error' in (response.data as any)
    ) {
      const businessError = (response.data as any).error
      Logger.error(
        `[${requestId}] DEBUG CLIENT: Business logic error for ${config.url}:`,
        businessError,
      )
      throw new Error(businessError)
    }

    // Return the response config object that the hooks expect
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    }
  } catch (error) {
    if (isCanceledRequestError(error)) {
      Logger.log(`[${requestId}] DEBUG CLIENT: Request canceled for ${config.url}`)
      throw error
    }

    Logger.error(`[${requestId}] DEBUG CLIENT: Request failed for ${config.url}:`, error)

    // Preserve the normalized error shape used by callers of this legacy wrapper. Generated
    // Kubb clients use the axios transport directly and normalize the same rejection themselves.
    if (axios.isAxiosError(error) && error.response) {
      const responseData = error.response.data as any
      const errorMessage =
        responseData?.error ||
        responseData?.error_msg ||
        responseData?.message ||
        `HTTP ${error.response.status} error`
      throw new ApiHttpError(errorMessage, {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: config.url,
      })
    }

    // Normalize all errors to standard Error objects for React Query
    if (error instanceof Error) {
      throw error
    }

    // Handle AxiosError and other unknown errors
    if (error && typeof error === 'object' && 'message' in error) {
      throw new Error(String(error.message))
    }

    // Fallback for completely unknown errors
    throw new Error('Unknown API error occurred')
  }
}

// Export fetch function for Kubb-generated hooks
export const fetch = client

export { persister }
export default client
